import { apiErrorText } from "../../../../api/api-error.js";
import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
/**
 * The shop's wait times, in the Orders tab (product owner, 2026-09-30: "at the end it should tell me
 * how long it will take, from settings in the customer portal and on the shop's own screen").
 *
 * Two whole-minute numbers — how long a delivery takes, and how long until a pickup order is ready.
 * The voice agent tells the caller the one that applies once the order is placed, from its next
 * conversation; the shop screen shows and changes the same two numbers; orders already placed keep
 * the wait they were given.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Field, Input } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import type { OrderBookGateway, ShopWaitTimeView } from "./order-book-gateway.js";

const WAIT_TIME_KEY = (shopId: string) => ["order-book-wait-time", shopId] as const;
const MINUTE_BOUND = { minimum: 1, maximum: 240 } as const;

function minuteOf(text: string): number | null {
  if (!/^\d{1,3}$/u.test(text.trim())) return null;
  const value = Number.parseInt(text.trim(), 10);
  return value >= MINUTE_BOUND.minimum && value <= MINUTE_BOUND.maximum ? value : null;
}

export function WaitTimePanel(props: { readonly gateway: OrderBookGateway; readonly shopId: string }): ReactNode {
  usePortalTranslation("operations");
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: WAIT_TIME_KEY(props.shopId),
    queryFn: () => props.gateway.shopWaitTime(props.shopId),
  });
  // `null` until edited: the field shows what is saved, and a save or a new read shows through.
  const [deliveryEdit, setDelivery] = useState<string | null>(null);
  const [pickupEdit, setPickup] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: (wait: ShopWaitTimeView) => props.gateway.setShopWaitTime(props.shopId, wait),
    onSuccess: (wait) => {
      queryClient.setQueryData(WAIT_TIME_KEY(props.shopId), wait);
      setDelivery(null);
      setPickup(null);
      setSaved(true);
    },
  });

  if (current.error !== null) {
    return <p className="c-note c-note-critical">{portalText("operations", "the_wait_times_could_not_be_read_6e9e702d")}{apiErrorText(current.error, "")}</p>;
  }
  if (current.data === undefined) return <p className="c-note">{portalText("operations", "reading_the_wait_times_b987065b")}</p>;

  const delivery = deliveryEdit ?? String(current.data.deliveryMinute);
  const pickup = pickupEdit ?? String(current.data.pickupMinute);
  const deliveryMinute = minuteOf(delivery);
  const pickupMinute = minuteOf(pickup);
  const invalid = portalText("operations", "a_whole_number_of_minutes_value_to_value_6faa3dc6", { v0: String(String(MINUTE_BOUND.minimum)), v1: String(String(MINUTE_BOUND.maximum)) });
  const unchanged = deliveryMinute === current.data.deliveryMinute && pickupMinute === current.data.pickupMinute;

  return (
    <section className="c-va-wait-time" aria-label={portalText("operations", "wait_times_7c0a87d7")}>
      <div>
        <h3 className="c-va-wait-title">{portalText("operations", "wait_times_7c0a87d7")}</h3>
        <p className="c-va-subtitle">
          {portalText("operations", "what_the_agent_tells_a_caller_once_the_order_is_placed_the_7f07a986")}</p>
      </div>
      <div className="c-va-wait-fields">
        <Field label={portalText("operations", "delivery_minutes_5848e158")} {...(deliveryMinute === null ? { error: invalid } : {})}>
          {(control) => (
            <Input
              {...control}
              type="number"
              inputMode="numeric"
              min={MINUTE_BOUND.minimum}
              max={MINUTE_BOUND.maximum}
              value={delivery}
              onChange={(event) => {
                setSaved(false);
                setDelivery(event.target.value);
              }}
            />
          )}
        </Field>
        <Field label={portalText("operations", "pickup_minutes_eebc2bef")} {...(pickupMinute === null ? { error: invalid } : {})}>
          {(control) => (
            <Input
              {...control}
              type="number"
              inputMode="numeric"
              min={MINUTE_BOUND.minimum}
              max={MINUTE_BOUND.maximum}
              value={pickup}
              onChange={(event) => {
                setSaved(false);
                setPickup(event.target.value);
              }}
            />
          )}
        </Field>
        <Button
          variant="secondary"
          disabled={deliveryMinute === null || pickupMinute === null || unchanged || save.isPending}
          onClick={() => {
            if (deliveryMinute === null || pickupMinute === null) return;
            save.mutate({ deliveryMinute, pickupMinute });
          }}
        >
          {save.isPending ? portalText("operations", "saving_56a2285c") : portalText("operations", "save_efc007a3")}
        </Button>
      </div>
      {save.error !== null ? <p className="c-note c-note-critical">{portalText("operations", "not_saved_cdefd1ee")}{apiErrorText(save.error, "")}</p> : null}
      {saved && save.error === null ? <p className="c-note">{portalText("operations", "saved_the_agent_says_it_from_its_next_call_3e3d28c7")}</p> : null}
    </section>
  );
}
